/* ===== HANDLE ADD TO CART ACTION ===== */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isloading = false;
        this.error = false;
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.addCartButtons = this.querySelectorAll('button[type="submit"]');
        this.errorMessageWrapper = document.getElementById(`Product-Form-Error-Message-${this.dataset.section}`);
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      get hideErrors() {
        return this.dataset.hideErrors === "true";
      }

      /* ===== ADD TO CART: SUBMIT HANDLER ===== */
      async onSubmitHandler(event) {
        if (this.isloading) return;
        event.preventDefault();

        if (
          Array.from(this.addCartButtons).find((button) =>
            button.hasAttribute("disabled"),
          )
        )
        return;

        /* ===== Personalization guard: block submit if text exceeds max ===== */
        if (!this.validatePersonalization()) return;
        /* ===== Recipient form guard: block submit if recipient fields are invalid ===== */
        if (!this.validateRecipientFields()) return;
        /* ===== Flyout selections guard: block submit if variant options or engraving fields are missing ===== */
        if (typeof window.__flyout?.validateFlyoutSelections === 'function') {
          if (!window.__flyout.validateFlyoutSelections()) return;
        }
        /* ===== Engraving Validation guard ===== */
        if (typeof window.__rcEngraving?.validate === 'function') {
          if (!window.__rcEngraving.validate()) return;
        }

        this.isloading = true;
        this.handleErrorMessage();
        this.startLoading();

        const config = webvista.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData  = new FormData(this.form);
        const variantId = formData.get("id");
        const uniqueKey = (Math.random() + 1).toString(36).substring(2);

        /* ===== Add Unique Key onto the main item ===== */
        formData.append("properties[_associated_unique_key]", uniqueKey);

        /* ===== Fetch Cart ===== */
        const cart = await this.fetchCart();

        /* ===== Find the selected variant is IDKMS {Boolean} ===== */
        const activeSizeOption = this.getSizeDropdownActiveOption();
        const text = (activeSizeOption?.dataset.value || activeSizeOption?.textContent || "")
          .trim()
          .toLowerCase()
          .replace(/['']/g, "'");
        const isIdkmsVariant = ["i don't know my size", "hold until i confirm my size", "send me a ring sizing gauge"].some((str) => text?.includes(str));

        if (isIdkmsVariant === true) {
          formData.append("properties[_is_idkms_included]", "true");
        }

        /* ===== CHECK PRODUCT TAGS ===== */
        const productTags = JSON.parse(this.dataset.tags || "[]");
        const dynamicImages = productTags.includes("dynamic-image-association");
        const isEngravable  = productTags.includes("Engravable");

        /* ===== Inject personalization properties (if applicable) ===== */
        this.appendPersonalizationProps(formData);
        /* ===== Inject toi-et-moi stone items with properties ===== */
        let nextIdx = this.appendToiEtMoiItems(formData, variantId, uniqueKey, 1, dynamicImages);
        /* ===== Inject all stone items product with properties ===== */
        nextIdx = this.appendStoneItems(formData, variantId, uniqueKey, nextIdx);
        /* ===== Inject add on flyout items with properties ===== */
        const flyoutStartIdx = nextIdx;
        nextIdx = this.appendFlyoutItems(formData, variantId, nextIdx);
        const flyoutEndIdx = nextIdx;
        /* ===== Inject Engraving ghost product ===== */
        this.appendEngravingGhost(formData, variantId, uniqueKey, nextIdx);
        /* ===== Inject Final Sale property if variant is marked final sale ===== */
        this.appendFinalSaleProperty(formData);
        /* ===== Inject Gift Wrap property if variant is marked gift wrap ===== */
        this.appendGiftWrapProperty(formData);
        /* ===== Inject IDKMS product if the variant option is IDKMS  ===== */
        this.appendIdkMySizeGhost(formData, variantId, uniqueKey, cart, isIdkmsVariant);
        /* ===== Inject Lead Time property for selected variant ===== */
        this.appendLeadTimeProperty(formData);

        /* ===== Sections so the drawer / bubble re-renders in the same response ===== */
        formData.append("sections", this.getSectionsToRender().map((s) => s.section).join(),);
        formData.append("sections_url", window.location.pathname);

        /* ===== Strip empty/disabled recipient properties so they are never sent ===== */
        this.stripEmptyRecipientProperties(formData);

        /* ===== CHECK ALLOW-MULTIPLE-PURCHASE TAG ===== */
        const allowMultiple = productTags.includes("allow-multiple-purchases");

        if (!allowMultiple) {
          /* ===== Check if this variant is already in the cart ===== */
          const existingLine = cart?.items?.find(
            (item) => String(item.variant_id) === String(variantId)
          );
          /* ===== UPDATE existing line: bump quantity + add any new flyout add-on items ===== */
          if (existingLine) {
            if (isEngravable) {
              /* ===== Engravable product, same variant re-submitted: refresh properties only — quantity is left exactly as-is ===== */
              await this.updateCartLineProperties(existingLine, formData, variantId, cart, uniqueKey, flyoutStartIdx, flyoutEndIdx);
            } else {
              /* ===== UPDATE existing line: bump quantity + add any new flyout add-on items ===== */
              await this.updateCartLine(existingLine, formData, variantId, cart, flyoutStartIdx, flyoutEndIdx);
            }
            return; // both handlers manage loading/error/events
          }
        }

        /* ===== Default path: add item(s) to cart ===== */
        config.body = formData;
        this.submitAddToCart(config, formData, variantId);
      }
      /* ===== FIND THE ACTIVE "Size" OPTION — works for BOTH the standard <select>-backed ===== */
      getSizeDropdownActiveOption() {
        /* ===== Case 1: Standard variant dropdown, backed by a real <select name="options[Size]"> ===== */
        const sizeSelect = this.form.querySelector('select[name="options[Size]"]');
        if (sizeSelect) {
          const dropdown = document.querySelector(`[data-select-id="${sizeSelect.id}"]`);
          const active = dropdown?.querySelector('.custom-dropdown__option.is-active');
          if (active) return active;
        }

        /* ===== Case 2: Bespoke / custom metafield dropdown — no <select> exists, so locate the correct .product-form-input by its "Size" label instead ===== */
        const sizeWrapper = [...document.querySelectorAll('.product-form-input')].find((wrapper) => {
          const label = wrapper.querySelector('.form-label');
          return label && label.textContent.trim().toLowerCase().startsWith('size');
        });

        return sizeWrapper?.querySelector('.custom-dropdown__option.is-active') || null;
      }

      /* ===== UPDATE AN EXISTING CART LINE + ALL ASSOCIATED COMPANIONS (quantity only) ===== */
      async updateCartLine(existingLine, formData, variantId, cart, flyoutStartIdx = 0, flyoutEndIdx = 0) {
        const quantity        = parseInt(formData.get("quantity") || "1", 10);
        const newMainQuantity = existingLine.quantity + quantity;

        /* ===== Find companion lines sharing the same _associated_unique_key ===== */
        const existingUniqueKey = existingLine.properties?.["_associated_unique_key"] || "";
        const companionLines    = existingUniqueKey
          ? (cart?.items || []).filter((item) => {
              return (
                item.properties?.["_associated_unique_key"] === existingUniqueKey &&
                item.key !== existingLine.key
              );
            })
          : [];

        /* ===== Build update tasks: quantity bump only, properties completely untouched ===== */
        const allUpdates = [
          {
            id:       String(existingLine.variant_id),
            quantity: newMainQuantity,
          },
          ...companionLines.map((companion) => ({
            id:       String(companion.variant_id),
            quantity: companion.quantity + quantity,
          })),
        ];

        try {
          /* ===== Add any NEW flyout add-on items first (independent items with their own unique keys) ===== */
          const flyoutAddForm = new FormData();
          let flyoutCount = 0;
          for (let i = flyoutStartIdx; i < flyoutEndIdx; i++) {
            if (!formData.has(`items[${i}][id]`)) continue;
            const prefix = `items[${i}]`;
            for (const [key, value] of [...formData.entries()]) {
              if (key.startsWith(prefix)) {
                flyoutAddForm.append(`items[${flyoutCount}]${key.slice(prefix.length)}`, value);
              }
            }
            flyoutCount++;
          }

          if (flyoutCount > 0) {
            const addConfig = webvista.fetchConfig("javascript");
            addConfig.headers["X-Requested-With"] = "XMLHttpRequest";
            delete addConfig.headers["Content-Type"];
            addConfig.body = flyoutAddForm;

            const addRes  = await fetch(`${window["routes"]["cart_add_url"]}`, addConfig);
            const addData = await addRes.json();

            if (addData.status) {
              webvista.publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form", productVariantId: variantId,
                errors: addData.errors || addData.description, message: addData.message,
              });
              this.handleErrorMessage(addData.description);
              this.error = true;
              return;
            }
          }

          let lastData = null;

          for (let i = 0; i < allUpdates.length; i++) {
            const isLast  = i === allUpdates.length - 1;
            const payload = {
              ...allUpdates[i],
              /* ===== Attach sections only on the last request so bubble re-renders once ===== */
              ...(isLast ? {
                sections:     this.getSectionsToRender().map((s) => s.section).join(","),
                sections_url: window.location.pathname,
              } : {}),
            };

            const res  = await fetch("/cart/change.js", {
              method:  "POST",
              headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
              body:    JSON.stringify(payload),
            });
            lastData = await res.json();

            if (lastData.status) {
              /* Main line error is fatal; companion errors are logged but non-fatal */
              if (i === 0) {
                webvista.publish(PUB_SUB_EVENTS.cartError, {
                  source: "product-form", productVariantId: variantId,
                  errors: lastData.errors || lastData.description, message: lastData.message,
                });
                this.handleErrorMessage(lastData.description);
                this.error = true;
                return;
              } else {
                console.warn("Failed to update companion line:", allUpdates[i].id, lastData);
              }
            }
          }

          this.error = false;

          SectionDynamicUpdate.updateSections(
            this.getSectionsToRender(),
            lastData["sections"],
          );

          webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: variantId,
            cartData: lastData,
          });

          /* ===== Capture flyout item counts BEFORE clearing data ===== */
          const stackItems = typeof window.__flyout?.getStackVariants === "function" ? window.__flyout.getStackVariants() : [];
          const styleItems = typeof window.__flyout?.getSelected === "function" ? window.__flyout.getSelected() : [];
          const hasFlyoutItems = stackItems.length > 0 || styleItems.length > 0;

          /* ===== Clear flyout state after successful update ===== */
          if (typeof window.__flyout?.empty === "function") {
            window.__flyout.empty();
          }
          /* ===== Clear personalization input after successful update ===== */
          this.clearPersonalizationInput();
          /* ===== Show Mini Cart Once the Items get Updated ===== */
          if (typeof itemAdded === "function") {
            itemAdded(hasFlyoutItems ? "multiple" : "single");
          }

        } catch {
          this.handleErrorMessage(window["accessibilityStrings"]["unknownError"]);
          this.error = true;
        } finally {
          this.endLoading();
        }
      }

      /* ===== UPDATE PROPERTIES ONLY ON AN EXISTING LINE (Engravable, non-allow-multiple products) ===== */
      /* Same variant re-submitted → refresh that line's properties (new engraving text/font/placement, */
      /* new personalization copy, etc). Quantity is left exactly as it was — never bumped, never reset. */
      async updateCartLineProperties(existingLine, formData, variantId, cart, uniqueKey, flyoutStartIdx = 0, flyoutEndIdx = 0) {
        /* ===== Build the new properties object for the main line from the flat properties[Foo] entries ===== */
        const properties = {};
        for (const [key, value] of [...formData.entries()]) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) {
            properties[match[1]] = value;
          }
        }

        /* ===== Keep the line's ORIGINAL unique key (not the freshly generated one) so any companion/ghost */
        /* lines already in the cart — engraving ghost, stones, etc. — stay associated with this line.      */
        const originalUniqueKey = existingLine.properties?.["_associated_unique_key"];
        const lineUniqueKey     = originalUniqueKey || uniqueKey;
        properties["_associated_unique_key"] = lineUniqueKey;

        /* ===== Is engraving required on THIS submission, and was there already a ghost line for it? ===== */
        const engravingNowRequired = properties["_engraving_required"] === "Yes";
        const existingGhostLine = (cart?.items || []).find((item) =>
          String(item.properties?.["_engrave_associated_var_id"]) === String(variantId) &&
          item.properties?.["_associated_unique_key"] === lineUniqueKey
        );

        try {
          /* ===== Engraving toggled ON since last submit + no ghost line yet: add it now, sharing this line's ORIGINAL unique key ===== */
          if (engravingNowRequired && !existingGhostLine) {
            const ghost = this.extractEngravingGhostFromFormData(formData, variantId);
            if (ghost && ghost.id) {
              const ghostAddForm = new FormData();
              ghostAddForm.append("items[0][id]", ghost.id);
              ghostAddForm.append("items[0][quantity]", "1");
              Object.entries(ghost.properties).forEach(([propKey, propValue]) => {
                // ===== Force the ghost onto the MAIN line's original unique key, not the freshly generated one =====
                const finalValue = propKey === "_associated_unique_key" ? lineUniqueKey : propValue;
                ghostAddForm.append(`items[0][properties][${propKey}]`, finalValue);
              });

              const ghostAddConfig = webvista.fetchConfig("javascript");
              ghostAddConfig.headers["X-Requested-With"] = "XMLHttpRequest";
              delete ghostAddConfig.headers["Content-Type"];
              ghostAddConfig.body = ghostAddForm;

              const ghostRes  = await fetch(`${window["routes"]["cart_add_url"]}`, ghostAddConfig);
              const ghostData = await ghostRes.json();

              if (ghostData.status) {
                webvista.publish(PUB_SUB_EVENTS.cartError, {
                  source: "product-form", productVariantId: variantId,
                  errors: ghostData.errors || ghostData.description, message: ghostData.message,
                });
                this.handleErrorMessage(ghostData.description);
                this.error = true;
                return;
              }
            }

          /* ===== Engraving toggled OFF since last submit + a ghost line still exists: remove it ===== */
          } else if (!engravingNowRequired && existingGhostLine) {
            const removeRes  = await fetch("/cart/change.js", {
              method:  "POST",
              headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
              body:    JSON.stringify({ id: existingGhostLine.key, quantity: 0 }),
            });
            const removeData = await removeRes.json();
            if (removeData.status) {
              console.warn("Failed to remove engraving ghost companion:", removeData);
            }

          /* ===== Engraving still ON and ghost already present: refresh its dynamic image if a new one was generated ===== */
          } else if (engravingNowRequired && existingGhostLine) {
            const ghost    = this.extractEngravingGhostFromFormData(formData, variantId);
            const newImage = ghost?.properties?.["_engraving_image"];
            if (newImage && newImage !== existingGhostLine.properties?.["_engraving_image"]) {
              const imgRes  = await fetch("/cart/change.js", {
                method:  "POST",
                headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
                body:    JSON.stringify({
                  id:         existingGhostLine.key,
                  quantity:   existingGhostLine.quantity,
                  properties: { ...existingGhostLine.properties, _engraving_image: newImage },
                }),
              });
              const imgData = await imgRes.json();
              if (imgData.status) {
                console.warn("Failed to refresh engraving ghost image:", imgData);
              }
            }
          }

          /* ===== Add any NEW flyout add-on items first (independent items with their own unique keys) ===== */
          const flyoutAddForm = new FormData();
          let flyoutCount = 0;
          for (let i = flyoutStartIdx; i < flyoutEndIdx; i++) {
            if (!formData.has(`items[${i}][id]`)) continue;
            const prefix = `items[${i}]`;
            for (const [key, value] of [...formData.entries()]) {
              if (key.startsWith(prefix)) {
                flyoutAddForm.append(`items[${flyoutCount}]${key.slice(prefix.length)}`, value);
              }
            }
            flyoutCount++;
          }

          if (flyoutCount > 0) {
            const addConfig = webvista.fetchConfig("javascript");
            addConfig.headers["X-Requested-With"] = "XMLHttpRequest";
            delete addConfig.headers["Content-Type"];
            addConfig.body = flyoutAddForm;

            const addRes  = await fetch(`${window["routes"]["cart_add_url"]}`, addConfig);
            const addData = await addRes.json();

            if (addData.status) {
              webvista.publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form", productVariantId: variantId,
                errors: addData.errors || addData.description, message: addData.message,
              });
              this.handleErrorMessage(addData.description);
              this.error = true;
              return;
            }
          }

          /* ===== Update the existing line's properties only — quantity is sent back unchanged ===== */
          const payload = {
            id:           existingLine.key,
            quantity:     existingLine.quantity,
            properties,
            sections:     this.getSectionsToRender().map((s) => s.section).join(","),
            sections_url: window.location.pathname,
          };

          const res  = await fetch("/cart/change.js", {
            method:  "POST",
            headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
            body:    JSON.stringify(payload),
          });
          const data = await res.json();

          if (data.status) {
            webvista.publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form", productVariantId: variantId,
              errors: data.errors || data.description, message: data.message,
            });
            this.handleErrorMessage(data.description);
            this.error = true;
            return;
          }

          this.error = false;

          SectionDynamicUpdate.updateSections(
            this.getSectionsToRender(),
            data["sections"],
          );

          webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: variantId,
            cartData: data,
          });

          /* ===== Capture flyout item counts BEFORE clearing data ===== */
          const stackItems = typeof window.__flyout?.getStackVariants === "function" ? window.__flyout.getStackVariants() : [];
          const styleItems = typeof window.__flyout?.getSelected === "function" ? window.__flyout.getSelected() : [];
          const hasFlyoutItems = stackItems.length > 0 || styleItems.length > 0;

          /* ===== Clear flyout state after successful update ===== */
          if (typeof window.__flyout?.empty === "function") {
            window.__flyout.empty();
          }
          /* ===== Clear personalization input after successful update ===== */
          this.clearPersonalizationInput();
          /* ===== Show Mini Cart Once the properties get updated ===== */
          if (typeof itemAdded === "function") {
            itemAdded(hasFlyoutItems ? "multiple" : "single");
          }

        } catch {
          this.handleErrorMessage(window["accessibilityStrings"]["unknownError"]);
          this.error = true;
        } finally {
          this.endLoading();
        }
      }

      /* ===== Pull the MAIN product's own engraving-ghost item (id + properties) out of formData. ===== */
      /* Matches on _engrave_associated_var_id === variantId to avoid picking up a flyout add-on's own  */
      /* engraving ghost, which uses the same property name but points at a different (add-on) variant. */
      extractEngravingGhostFromFormData(formData, variantId) {
        let ghostIdx = null;
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^items\[(\d+)\]\[properties\]\[_engrave_associated_var_id\]$/);
          if (match && String(value) === String(variantId)) {
            ghostIdx = match[1];
            break;
          }
        }
        if (ghostIdx === null) return null;

        const prefix = `items[${ghostIdx}]`;
        const ghost  = { id: formData.get(`${prefix}[id]`), properties: {} };
        for (const [key, value] of formData.entries()) {
          if (!key.startsWith(prefix)) continue;
          const propMatch = key.match(/^items\[\d+\]\[properties\]\[(.+)\]$/);
          if (propMatch) ghost.properties[propMatch[1]] = value;
        }
        return ghost;
      }

      /* ===== SUBMIT ADD TO CART (standard path) ===== */
      submitAddToCart(config, formData, variantId) {
        fetch(`${window["routes"]["cart_add_url"]}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              /* ===== If a GHOST/companion item is sold out, retry with the main product alone ===== */
              const hasGhostItems = [...formData.keys()].some((key) =>
                /^items\[\d+\]\[id\]$/.test(key)
              );
              const productTags = JSON.parse(this.dataset.tags || "[]");
              const isCloudPearStone = productTags.includes("cloud-pear-stone");

              if (isCloudPearStone && hasGhostItems && this.isSoldOutError(response)) {
                const mainOnlyFormData = this.buildMainItemOnlyFormData(formData);
                const retryConfig = webvista.fetchConfig("javascript");
                retryConfig.headers["X-Requested-With"] = "XMLHttpRequest";
                delete retryConfig.headers["Content-Type"];
                retryConfig.body = mainOnlyFormData;

                this.submitAddToCart(retryConfig, mainOnlyFormData, variantId);
                return;
              }

              webvista.publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            this.error = false;

            SectionDynamicUpdate.updateSections(
              this.getSectionsToRender(),
              response["sections"],
            );

            webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: variantId,
              cartData: response,
            });

            /* ===== Capture flyout item counts BEFORE clearing data ===== */
            const stackItems = typeof window.__flyout?.getStackVariants === "function" ? window.__flyout.getStackVariants() : [];
            const styleItems = typeof window.__flyout?.getSelected === "function" ? window.__flyout.getSelected() : [];
            const hasFlyoutItems = stackItems.length > 0 || styleItems.length > 0;

            /* ===== Clear flyout state after successful add ===== */
            if (typeof window.__flyout?.empty === "function") {
              window.__flyout.empty();
            }
            /* ===== Clear personalization input after successful add ===== */
            this.clearPersonalizationInput();
            /* ===== Show Mini Cart Once the Items get Added ===== */
            if (typeof itemAdded === "function") {
              itemAdded(hasFlyoutItems ? "multiple" : "single");
            }
          })
          .catch(() => {
            this.handleErrorMessage(
              window["accessibilityStrings"]["unknownError"],
            );
            this.error = true;
          })
          .finally(() => {
            this.endLoading();
          });
      }

      /* ===== GIFT CARD PRODUCT: RECIPIENT FORM VALIDATION ===== */
      validateRecipientFields() {
        const sectionId = this.dataset.section;
        const recipientForm = document.querySelector(`recipient-form[data-section="${sectionId}"]`);
        if (!recipientForm || typeof recipientForm.validateRecipientForm !== "function") {
          return true;
        }
        return recipientForm.validateRecipientForm();
      }

      /* ===== GIFT CARD PRODUCT: RECIPIENT FORM PROPERTY CLEANUP ===== */
      stripEmptyRecipientProperties(formData) {
        const sectionId = this.dataset.section;
        const recipientForm = document.querySelector(
          `recipient-form[data-section="${sectionId}"]`,
        );
        // The full list of recipient property keys managed by this form
        const recipientKeys = [
          "properties[Recipient name]",
          "properties[Recipient email]",
          "properties[Message]",
          "properties[Send on]",
          "properties[__shopify_offset]",
          "properties[__shopify_send_gift_card_to_recipient]",
        ];

        const isActive =
          recipientForm &&
          typeof recipientForm.isRecipientFormActive === "function" &&
          recipientForm.isRecipientFormActive();

        if (!isActive) {
          // Checkbox is unchecked — remove all recipient properties entirely
          recipientKeys.forEach((key) => formData.delete(key));
          return;
        }
        // Checkbox is checked — only remove keys whose value is blank
        recipientKeys.forEach((key) => {
          const val = formData.get(key);
          if (val !== null && val.trim() === "") {
            formData.delete(key);
          }
        });
      }

      /* ===== PERSONALIZATION HELPERS ===== */
      hasPersonalization() {
        return !!document.getElementById("rc-personalization-wrapper");
      }
      /* ===== VALIDATE PERSONALIZATION BEFORE ATC ===== */
      validatePersonalization() {
        if (!this.hasPersonalization()) return true;
        const api = window.__rcPersonalization;
        if (!api) return true;
        return api.validate(); /* error display is handled inside the liquid */
      }
      /* ===== ADD PERSONALIZATION DATA/PROPERTIES ===== */
      appendPersonalizationProps(formData) {
        if (!this.hasPersonalization()) return;

        const api      = window.__rcPersonalization;
        const text     = api ? api.getText().trim() : (formData.get("properties[Customization]") || "").trim();
        const len      = text.length;
        /* Read whether customization is required or optional from the wrapper */
        const wrapper    = document.getElementById("rc-personalization-wrapper");
        const isRequired = wrapper?.dataset.required !== "false";

        /* Overwrite the raw form value with the trimmed version */
        formData.delete("properties[Customization]");
        formData.append("properties[Customization]", text);
        /* Companion properties */
        formData.append("properties[_Customization_Required]", isRequired ? "Yes" : "No");
        formData.append("properties[_Customization_Word_Count]", String(len));
      }

      /* ===== Resets the personalization input after a successful add-to-cart. ===== */
      clearPersonalizationInput() {
        const input = document.getElementById("rc-personalization-input");
        if (!input) return;
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }

      /* ===== ADD LEAD TIME PROPERTY ===== */
      appendLeadTimeProperty(formData) {
        /* ===== Engraving ON: the engraving display holds the effective lead time (e.g. "In Stock. Please add an additional 2-3 weeks for engraving.") ===== */
        const engravingContainer = document.getElementById('rc-leadtime-display-engraving');
        const engravingOn = engravingContainer && !engravingContainer.hidden;

        const container = engravingOn
          ? engravingContainer
          : (document.getElementById('rc-leadtime-display') || document.getElementById('rc-leadtime-display-locket-charms'));

        if (!container || container.hidden) return;

        const leadTime = container.textContent.trim();
        if (leadTime) {
          /* ===== Overwrite any existing value so we never send a duplicate/stale Lead Time ===== */
          formData.delete('properties[Lead Time]');
          formData.append('properties[Lead Time]', leadTime);
        }
      }

      /* ===== FORMDATA APPENDERS: MAIN PRODUCT ===== */
      appendMainItemIndexed(formData, variantId, uniqueKey) {
        formData.append("items[0][id]",       variantId);
        formData.append("items[0][quantity]",  formData.get("quantity") || "1");
        /* Copy every flat properties[Foo] → items[0][properties][Foo] */
        for (const [key, value] of [...formData.entries()]) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) {
            formData.append(`items[0][properties][${match[1]}]`, value);
          }
        }
        /* ===== Ensure unique key is on item 0 ===== */
        formData.append("items[0][properties][_associated_unique_key]", uniqueKey);
      }

      /* ===== ADD FINAL SALE PROPERTY ===== */
      appendFinalSaleProperty(formData) {
        const msg = document.getElementById(`FinalSaleMsg-${this.dataset.section}`);
        if (!msg) return;

        const isFinalSale        = msg.textContent.trim().length > 0;
        const isAvailable        = msg.dataset.available === "true";
        // If engraving toggled this on, trust it regardless of variant availability
        const isEngravingOwned   = msg.dataset.engravingFinalSale === 'true';

        if (isFinalSale && (isAvailable || isEngravingOwned)) {
          // Don't double-append if engraving already injected the hidden input
          if (!formData.get("properties[Final_Sale]")) {
            formData.append("properties[Final_Sale]", "(Final Sale!)");
          }
          if (!formData.get("properties[_excludeFromNarvar]")) {
            formData.append("properties[_excludeFromNarvar]", "yes");
          }
        } else {
          // Not final sale (or unavailable/not engraving-owned) — explicitly mark as included in Narvar
          if (!formData.get("properties[_excludeFromNarvar]")) {
            formData.append("properties[_excludeFromNarvar]", "no");
          }
        }
      }

      /* ===== ADD GIFT WRAP PROPERTY ===== */
      appendGiftWrapProperty(formData) {
        const msg = document.getElementById(`GiftWrapMsg-${this.dataset.section}`);
        if (!msg) return;
        if (msg.dataset.giftWrap === "true") {
          formData.append("properties[_giftwrap]", "true");
        }
      }

      /* ===== ADD TOI ET MOI STONE ITEMS WITH PROPERTIES ===== */
      appendToiEtMoiItems(formData, mainVarId, uniqueKey, startIdx = 1, dynamicImages = false) {
        let idx = startIdx;
        const LABEL_MAP = {
          1: { titleKey: 'Emerald-Stone', vidKey: '_Emerald_Stone_v_id' },
          2: { titleKey: 'Pear-Stone',    vidKey: '_Pear_Stone_v_id'    },
        };
        const stoneData = window.__rcToiStoneData || {};

        /* ===== Iterate in slot order (1 = emerald, 2 = pear) so cart items are consistent ===== */
        for (const i of [1, 2]) {
          const entry = stoneData[i];
          if (!entry?.variantId?.trim()) continue;  /* not on this PDP or not selected → skip */

          const { variantId, title, imgSrc } = entry;
          const labels = LABEL_MAP[i];

          /* ===== Ghost item for this stone ===== */
          formData.append(`items[${idx}][id]`,      variantId);
          formData.append(`items[${idx}][quantity]`, "1");
          formData.append(`items[${idx}][properties][_associated_unique_key]`,     uniqueKey);
          formData.append(`items[${idx}][properties][_stone_associated_variant_id]`, mainVarId);
          /* ===== Update MAIN product properties ===== */
          formData.append(`properties[${labels.titleKey}]`, title);      
          formData.append(`properties[${labels.vidKey}]`,   variantId); 

          /* ===== Add selected combination image to the properties (only if product has dynamic-image-association tag) ===== */
          if (dynamicImages) {
            const toiMoiImage = window.__rcToiMoiGeneratedImage || imgSrc;
            if (toiMoiImage) {
              formData.delete('properties[_toi_moi_image]');
              formData.append('properties[_toi_moi_image]', toiMoiImage);
            }
          }
          idx++;
        }
        return idx;
      }

      /* ===== ADD STONE PRODUCTS WITH ASSOCIATED PROPERTIES ===== */
      appendStoneItems(formData, mainVarId, uniqueKey, startIdx = 1) {
        const stoneTag = formData.get("properties[_customStoneTag]") || "";
        const isLocket = stoneTag === "locket-charms";
        let idx = startIdx;
        
        /* ===== Grab count from active dropdown and append as property ===== */
        if (isLocket) {
          const countValue = document.querySelector(".rc-custom-locket-charm-dropdown.is-active")?.dataset.value;
          if (countValue) {
            formData.append("properties[Charm_Count]", String(countValue));
          }
        }

        for (let i = 1; i <= 20; i++) {
          const stoneVarId = formData.get(`properties[_Custom_Stone_${i}]`);
          if (stoneVarId === null) break;                      /* no input → stop */
          if (!stoneVarId || !stoneVarId.trim()) continue;    /* empty    → skip */

          const title = formData.get(`properties[_Custom_Stone_Title_${i}]`) || "";
          // Read lead time from the active option for this stone picker
          const activeOpt = document.querySelector(`.rc-stone-picker-wrapper[data-stone-index="${i}"] .custom-dropdown__option.is-active`);
          const resolvedStatus = activeOpt?.querySelector('.rc-inject-variant-leadtime .rc-stone-lt-label__status')?.textContent?.trim() || '';

          formData.append(`items[${idx}][id]`,       stoneVarId);
          formData.append(`items[${idx}][quantity]`,  "1");
          formData.append(`items[${idx}][properties][_associated_unique_key]`,       uniqueKey);
          formData.append(`items[${idx}][properties][_stone_associated_variant_id]`, mainVarId);
          formData.append(`items[${idx}][properties][_stone_index]`,                 String(i));
          formData.append(`items[${idx}][properties][_customStoneTag]`,              stoneTag);
          formData.append(`items[${idx}][properties][_stone_title]`,                 title);
          // Add lead time if present
          if (resolvedStatus && resolvedStatus !== 'In Stock') {
            const customLeadTime = activeOpt?.dataset.leadTime?.trim() || resolvedStatus;
            formData.append(`items[${idx}][properties][Lead Time]`, customLeadTime);
          } else if (resolvedStatus === 'In Stock') {
            formData.append(`items[${idx}][properties][Lead Time]`, 'In Stock');
          }
          idx++;
        }

        return idx; /* hand next free slot to the next appender */
      }

      /* ===== ADD FYLOUT ITEMS WITH PROPERTIES ===== */
      appendFlyoutItems(formData, mainVarId, startIdx = 1) {
        let idx = startIdx;

        /* ===== Stack-with items ===== */
        const stackItems = typeof window.__flyout?.getStackVariants === "function"
          ? window.__flyout.getStackVariants()
          : [];

        const stackPIds = stackItems.map((v) => v.productid);

        stackItems.forEach((product) => {
          const key = (Math.random() + 1).toString(36).substring(2);
          formData.append(`items[${idx}][id]`,       product.id);
          formData.append(`items[${idx}][quantity]`,  "1");
          formData.append(`items[${idx}][properties][_stack_with_associated_variant_id]`, mainVarId);
          formData.append(`items[${idx}][properties][Lead Time]`,                         product.leadtime || "");
          formData.append(`items[${idx}][properties][_associated_unique_key]`,            key);
          idx++;
        });

        /* ===== Style-with items: Engraving Products ===== */
        const styleItems = typeof window.__flyout?.getSelected === "function"
          ? window.__flyout.getSelected().filter((p) => !stackPIds.includes(p.pId))
          : [];

        styleItems.forEach((product) => {
          const key         = (Math.random() + 1).toString(36).substring(2);
          const engToggle   = document.getElementById(`engravablesInputsWrapperMejuri${product.id}`);
          const engravingOn = product.engraving?.enabled && engToggle?.checked;

          if (engravingOn) {
            const fontVal      = document.getElementById(`selected-font-${product.pId}`)?.value || "";
            const placementVal = document.getElementById(`selected-placement-${product.pId}`)?.value || "";
            const textOne      = document.querySelector(`.flyout-engraving-text-one-${product.pId}`)?.value || "";
            const textTwo      = document.querySelector(`.flyout-engraving-text-two-${product.pId}`)?.value || "";
            const isCeline     = fontVal === "Celine";

            /* ===== Engraving ghost for this flyout item ===== */
            formData.append(`items[${idx}][id]`,       engravingProductId);
            formData.append(`items[${idx}][quantity]`,  "1");
            formData.append(`items[${idx}][properties][_engrave_associated_var_id]`, product.id);
            formData.append(`items[${idx}][properties][_associated_unique_key]`,     key);
            idx++;

            /* ===== Style item itself with engraving props ===== */
            formData.append(`items[${idx}][id]`,       product.id);
            formData.append(`items[${idx}][quantity]`,  "1");
            formData.append(`items[${idx}][properties][_style_with_associated_variant_id]`, mainVarId);
            formData.append(`items[${idx}][properties][Lead Time]`,                         product.leadtime || "");
            formData.append(`items[${idx}][properties][_associated_unique_key]`,            key);
            formData.append(`items[${idx}][properties][_engraving_required]`,               "Yes");
            formData.append(`items[${idx}][properties][Final_Sale]`, "(Final Sale!)");
            formData.append(`items[${idx}][properties][_excludeFromNarvar]`, "yes");
            if (fontVal)           formData.append(`items[${idx}][properties][Engraving_Font]`,     fontVal);
            if (placementVal)      formData.append(`items[${idx}][properties][_engravingplacement]`, placementVal);
            if (textOne)           formData.append(`items[${idx}][properties][Engraving_Text]`,      isCeline ? textOne.toUpperCase() : textOne);
            if (textTwo)           formData.append(`items[${idx}][properties][Engraving_Text_2]`,    isCeline ? textTwo.toUpperCase() : textTwo);
            idx++;

          } else {
            formData.append(`items[${idx}][id]`,       product.id);
            formData.append(`items[${idx}][quantity]`,  "1");
            formData.append(`items[${idx}][properties][_style_with_associated_variant_id]`, mainVarId);
            formData.append(`items[${idx}][properties][Lead Time]`,                         product.leadtime || "");
            formData.append(`items[${idx}][properties][_associated_unique_key]`,            key);
            idx++;
          }
        });

        return idx;
      }

      /* ===== ADD ENGRAVING PRODUCT WITH PROPERTIES ===== */
      appendEngravingGhost(formData, mainVarId, uniqueKey, startIdx = 1) {
        if (formData.get("properties[_engraving_required]") !== "Yes") return;

        /* Find next free slot by counting how many items[N] are already set */
        let idx = startIdx;
        while (formData.has(`items[${idx}][id]`)) idx++;

        formData.append(`items[${idx}][id]`,       engravingProductId);
        formData.append(`items[${idx}][quantity]`,  "1");
        formData.append(`items[${idx}][properties][_engrave_associated_var_id]`, mainVarId);
        formData.append(`items[${idx}][properties][_associated_unique_key]`,     uniqueKey);
        const engravingImage = window.__rcEngravingGeneratedImage;
        if (engravingImage) {
          formData.append(`items[${idx}][properties][_engraving_image]`, engravingImage);
        }
      }

      /* ===== Detect a "sold out" failure so we can retry without ghost/companion items ===== */
      isSoldOutError(response) {
        const text = `${response.message || ""} ${response.description || ""}`.toLowerCase();
        return (
          text.includes("sold out") ||
          text.includes("not available") ||
          text.includes("out of stock")
        );
      }

      /* ===== Strip every ghost/companion items[N][...] entry, keep only the main product's own fields ===== */
      buildMainItemOnlyFormData(formData) {
        const mainFormData = new FormData();
        for (const [key, value] of formData.entries()) {
          if (/^items\[\d+\]/.test(key)) continue; // drop every indexed ghost/companion item
          mainFormData.append(key, value);
        }
        return mainFormData;
      }

      /* ===== FETCH CART ===== */
      async fetchCart() {
        try {
          const response = await fetch(window["routes"]["cart_url"] + ".js", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
          });
          return await response.json();
        } catch (error) {
          console.error("Failed to fetch cart:", error);
          return null;
        }
      }

      /* ===== INJECT IDK MY SIZE GHOST PRODUCT (only if not already in cart) ===== */
      appendIdkMySizeGhost(formData, variantId, uniqueKey, cart, isIdkmsVariant) {
        if (!isIdkmsVariant) return;

        /* ===== Check if ghost product is already in the cart ===== */
        const alreadyInCart = cart?.items?.some(
          (item) => String(item.variant_id) === String(idkmsVariantId) // idkmsVariantId is defined in rc-global-variables-pdp.liquid
        );

        /* ===== IDK my size ghost already in cart, skipping... ===== */
        if (alreadyInCart) {
          return;
        }

        /* ===== Find next free slot ===== */
        let idx = 1;
        while (formData.has(`items[${idx}][id]`)) idx++;

        formData.append(`items[${idx}][id]`, idkmsVariantId); // idkmsVariantId is defined in rc-global-variables-pdp.liquid
        formData.append(`items[${idx}][quantity]`, "1");
        // formData.append(`items[${idx}][properties][_associated_unique_key]`, uniqueKey);
      }

      /* ===== CHECK IF SELECTED SIZE OPTION IS IDKMS ===== */
      isIdkMySizeSelected() {
        /* ===== Option inputs can be selects, radios, or hidden inputs depending on theme ===== */
        const sizeSelect = this.form.querySelector('select[name="Size"], input[name="Size"], [data-option-name="Size"]');
        if (sizeSelect) {
          return sizeSelect.value.toLowerCase().includes("i don't know");
        }

        /* ===== Fallback: check all option inputs ===== */
        const allOptions = [...this.form.querySelectorAll('[name^="options["]')];
        return allOptions.some((opt) =>
          opt.value.toLowerCase().includes("i don't know")
        );
      }

      /* ===== STANDARD HELPERS ===== */
      getSectionsToRender() {
        return [
          {
            id:       "Cart-Icon-Bubble",
            section:  "cart-icon-bubble",
            selector: ".shopify-section",
          },
        ];
      }

      handleErrorMessage(errorMessage = null) {
        if (this.hideErrors || !this.errorMessageWrapper) return;
        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        this.errorMessageWrapper.textContent = errorMessage ? errorMessage : "";
      }

      startLoading() {
        this.isloading = true;
        this.addCartButtons.forEach((button) => {
          button.setAttribute("disabled", "disabled");
          button.classList.add("loading");
        });
      }

      endLoading() {
        this.isloading = false;
        this.addCartButtons.forEach((button) => {
          button.removeAttribute("disabled");
          button.classList.remove("loading");
        });
      }
    },
  );
}